import React from "react";
import { Option, RawFile } from "../model";
import { useAuth } from "app/authHooks";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { Folder, Plus, EmptyPage, Xmark } from "iconoir-react";
import Text from "../components/Text";
import Uploader from "../components/Uploader";
import { bytesToSize } from "../utils/file";
import { LoadingButton } from "@mui/lab";
import useMessage from "../hooks/useMessage";
import { DATE_FORMAT } from "../utils/date";
import { format } from "date-fns";
import { first, isEmpty, isNil, keys } from "lodash";
import AmlDocument from "../components/AmlDocument";
import useBoolean from "../hooks/useBoolean";

const OTHER_AML_DOCUMENT_CATEGORY_CODE = "OTHER";
// 10MB
const DEFAULT_MAX_FILE_BYTE = 10 * 1024 * 1024;

type CategoryOption = Option & { docVaultCategory: string };

export type AmlDocumentUploadModalProps = {
  open: boolean;
  investorId: string;
  initialFiles?: RawFile[];
  fileUsage: React.ReactNode;
  maxFileByte?: number;
  maxFileNum?: number;
  categoryOptions: CategoryOption[];
  onClose: () => void;
  onComplete: (docs: RawFile[]) => void;
};

type IdFiles = File & { uid: string };

type FileError = { uid: string; field: string; msg: string };
type FileRef = {
  validate: (field?: string) => FileError[] | undefined;
};

const AmlDocumentUploadModal: React.FC<AmlDocumentUploadModalProps> = ({
  open,
  investorId,
  initialFiles = [],
  fileUsage,
  maxFileByte = DEFAULT_MAX_FILE_BYTE,
  maxFileNum = -1,
  categoryOptions,
  onClose,
  onComplete,
}) => {
  const [files, setFiles] = React.useState<RawFile[]>(initialFiles);
  const [submitting, { setTrue: startSubmitting, setFalse: finishSubmitting }] =
    useBoolean();
  const { attributes } = useAuth();
  const message = useMessage();
  const currentEmployeeName = attributes.name;
  const currentCustomerId = attributes["custom:customer"];
  // refs of File component to check fields required.
  const fileRefs = React.useRef<Record<string, FileRef>>({});

  React.useEffect(() => {
    // clear files when close.
    if (!open) {
      setFiles([]);
    }
  }, [open]);

  const handleFileAdded = (newFiles: IdFiles[]) => {
    newFiles.forEach((newFile) => {
      setFiles((pre) => {
        if (!validateNewFile(newFile, pre)) {
          return pre;
        }
        const fileObj: RawFile = {
          uid: newFile.uid,
          name: newFile.name,
          size: newFile.size,
          amlCategoryCode: "",
          uploader: currentEmployeeName,
          uploadTime: format(new Date(), DATE_FORMAT),
          _raw: newFile,
          metadata: {},
        };
        return [...pre, fileObj];
      });
    });
  };

  const validateNewFile = (file: IdFiles, currentFiles: RawFile[]) => {
    if (files.some((f) => f.name === file.name)) {
      message.error("Cannot upload duplicate files.");
      return false;
    }
    if (file.size > maxFileByte) {
      message.error(
        `Cannot upload file, size exceeds ${bytesToSize(maxFileByte, 0)}.`
      );
      return false;
    }
    if (maxFileNum > 0 && currentFiles.length >= maxFileNum) {
      message.error(
        `Sorry, you can only upload up to ${maxFileNum} file${
          maxFileNum > 1 ? "s" : ""
        }.`
      );
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!validateFiles()) {
      return;
    }
    const fileObjects = files.map((file) => buildFileObject(file));
    startSubmitting();
    try {
      await onComplete(fileObjects);
      onClose();
      finishSubmitting();
    } catch (err) {
      console.error(err);
      finishSubmitting();
    }
  };
  const buildFileObject = (file: RawFile): RawFile => {
    // build metadata, this is used when uploading file to Doc Vault
    const metadata = {
      customerId: currentCustomerId,
      investorId,
    };
    // remove public checkbox and default to true since all documents are investor-related.
    file.isPublic = true;
    file.metadata = {
      ...metadata,
      ...file.metadata,
    };
    return file;
  };
  const validateFiles = () => {
    let valid = true;
    for (const key of keys(fileRefs.current)) {
      const ref = fileRefs.current[key];
      if (ref) {
        valid = valid && ref.validate() === undefined;
      }
    }
    return valid;
  };

  const handleFileRemove = (file: RawFile) => {
    // remove ref as well
    delete fileRefs.current[file.uid];
    setFiles((pre) => pre.filter((el) => el !== file));
  };
  const handleFileUpdate = () => {
    setFiles((pre) => [...pre]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          p: 0,
          width: window.innerWidth > 1280 ? "800px" : "80%",
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            fontSize: "1.2rem",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <Folder />
          Upload Document
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" rowGap="10px">
          <Text>{fileUsage}</Text>
          <Box
            className="mg-top-10 mg-bottom-10"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              maxHeight: "230px",
              overflow: "auto",
              padding: "4px",
              margin: "0 -4px -4px",
            }}
          >
            {files.map((file) => (
              <File
                ref={(el) => {
                  if (el) {
                    fileRefs.current[file.uid] = el;
                  }
                }}
                file={file}
                categoryOptions={categoryOptions}
                onUpdate={handleFileUpdate}
                onRemove={handleFileRemove}
              />
            ))}
          </Box>
          <Uploader multiple onFilesSelected={handleFileAdded}>
            {({ openSystemFileDialog }) => {
              return (
                <Box
                  sx={{
                    height: "60px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: "2px",
                    border: "1.5px dashed rgb(220, 220, 220)",
                    backgroundColor: "rgb(246, 246, 246)",
                    cursor: "pointer",
                  }}
                  onClick={openSystemFileDialog}
                >
                  <Button
                    sx={{ borderRadius: "2px", padding: "1px 9px" }}
                    startIcon={<Plus />}
                  >
                    Add File
                  </Button>
                </Box>
              );
            }}
          </Uploader>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Text italic sx={{ p: "10px", fontSize: "0.8em" }}>
            * Recommended file size is {bytesToSize(maxFileByte, 0)}
          </Text>
          <Box>
            <Button
              variant="text"
              sx={{
                backgroundColor: "transparent",
                color: "primary.main",
                padding: "6px 8px",
                minWidth: "64px",
                fontSize: "0.875rem",
                fontWeight: 500,
                lineHeight: 1.75,
                borderRadius: "4px",
                textTransform: "uppercase",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  boxShadow: "none",
                },
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              sx={{ borderRadius: "4px", marginLeft: "6px" }}
              onClick={handleUpload}
              loading={submitting}
            >
              Upload
            </LoadingButton>
          </Box>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AmlDocumentUploadModal;

type FileProps = {
  file: RawFile;
  categoryOptions: CategoryOption[];
  onUpdate: (f: RawFile) => void;
  onRemove: (f: RawFile) => void;
};

export const DocumentStatus = {
  PENDING: "PENDING",
  UPLOADING: "UPLOADING",
  SUCCESS: "SUCCESS",
  ERROR: "ERROR",
} as const;

export const DocumentStatusColor = {
  SUCCESS: "#276738",
  ERROR: "#ff4d4f",
  PENDING: "#faad14",
  UPLOADING: "#faad14",
};

const File = React.forwardRef<FileRef, FileProps>(
  ({ file, categoryOptions, onUpdate, onRemove }, ref) => {
    const [errors, setErrors] = React.useState<FileError[]>([]);
    const getErrors = (field: string) => {
      return errors.filter((el) => el.field === field);
    };
    const handleCategoryChange = (value: string, option: CategoryOption) => {
      file.metadata.category = option.docVaultCategory;
      file.amlCategoryCode = value;
      file.amlCategoryDescription = option.label;
      if (value !== OTHER_AML_DOCUMENT_CATEGORY_CODE) {
        file.otherTypeDescription = undefined;
      }
      onUpdate(file);
      validate("category");
    };
    const handleCategoryDescriptionChange = (value: string) => {
      file.otherTypeDescription = value;
      onUpdate(file);
      validate("otherTypeDescription");
    };
    const rules = [
      {
        field: "category",
        validator: () => {
          if (isNil(file.metadata.category)) {
            return "This Field is Required.";
          }
        },
      },
      {
        field: "otherTypeDescription",
        // when category code is OTHER, request for customized category description.
        validator: () => {
          if (
            file.amlCategoryCode === OTHER_AML_DOCUMENT_CATEGORY_CODE &&
            (isNil(file.otherTypeDescription) ||
              isEmpty(file.otherTypeDescription))
          ) {
            return "This Field is Required.";
          }
        },
      },
    ];
    const validate: FileRef["validate"] = (field?: string) => {
      const errorList: FileError[] = [];
      // when field is nullish, check all rules.
      const candidateRules = field
        ? rules.filter((el) => el.field === field)
        : rules;
      candidateRules.forEach((rule) => {
        const errorMsg = rule.validator();
        if (errorMsg) {
          errorList.push({
            uid: file.uid,
            field: rule.field,
            msg: errorMsg,
          });
        }
      });
      setErrors(errorList);
      return errorList.length > 0 ? errorList : undefined;
    };
    // enable parent to dicide when to validate file.
    React.useImperativeHandle(ref, () => ({
      validate,
    }));
    const handleRemove = () => {
      onRemove(file);
    };
    return (
      <AmlDocument.Root key={file.uid}>
        <AmlDocument.Status status="SUCCESS" />
        <AmlDocument.Property label="File properties" sx={{ width: "180px" }}>
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "100%",
                gap: "4px",
              }}
            >
              <EmptyPage
                style={{
                  fill: "#7e7e7e",
                  strokeWidth: "0",
                  fontSize: "0.9rem",
                  flexShrink: 0,
                }}
              />
              <Tooltip title={file.name} placement="top">
                <Text ellipsis={1} sx={{ fontSize: "0.9em" }}>
                  {file.name}
                </Text>
              </Tooltip>
            </Box>
            <Text
              italic
              sx={{
                marginLeft: "calc(1rem + 4px)",
                paddingLeft: "4px",
                fontSize: "0.8em",
              }}
            >
              {bytesToSize(file.size)}
            </Text>
          </Box>
        </AmlDocument.Property>
        <AmlDocument.Property label="Uploader">
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <Text sx={{ fontSize: "0.9em", lineHeight: "0.9em" }}>
              {file.uploader}
            </Text>
            <Text
              sx={{
                fontSize: "0.8em",
                lineHeight: "0.8em",
                fontStyle: "italic",
              }}
            >
              {file.uploadTime}
            </Text>
          </Box>
        </AmlDocument.Property>
        <AmlDocument.Property label="Category *">
          <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <TextField
              size="small"
              select
              sx={{
                width: "192px",
                "& .MuiInputBase-root": {
                  height: 32,
                  fontSize: "0.75rem",
                },
                "& input": {
                  padding: "6px 8px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              onChange={(e) => {
                const value = e.target.value;
                const option = categoryOptions.find((el) => el.value === value);
                handleCategoryChange(value, option!);
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      p: 0,
                    },
                  },
                },
              }}
            >
              {categoryOptions.map((el) => (
                <MenuItem value={el.value}>{el.label}</MenuItem>
              ))}
            </TextField>
            {getErrors("category").length > 0 && (
              <Text sx={{ color: "#ff4d4f" }}>
                {first(getErrors("category"))?.msg}
              </Text>
            )}
          </Box>
        </AmlDocument.Property>
        {file.amlCategoryCode === OTHER_AML_DOCUMENT_CATEGORY_CODE && (
          <AmlDocument.Property label="Category Description">
            <Box sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
              <TextField
                size="small"
                inputProps={{ maxLength: 50 }}
                onChange={(e) =>
                  handleCategoryDescriptionChange(e.currentTarget.value)
                }
                value={file.otherTypeDescription}
              />
              {getErrors("otherTypeDescription").length > 0 && (
                <Text sx={{ color: "#ff4d4f" }}>
                  {first(getErrors("otherTypeDescription"))?.msg}
                </Text>
              )}
            </Box>
          </AmlDocument.Property>
        )}
        <AmlDocument.Controller>
          <IconButton
            sx={{
              height: "24px",
              width: "28px",
              borderRadius: "4px",
              backgroundColor: "#ff4d4f",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#ff4d4f",
              },
            }}
            onClick={handleRemove}
          >
            <Xmark />
          </IconButton>
        </AmlDocument.Controller>
      </AmlDocument.Root>
    );
  }
);
